/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Push-based invalidation for git provider integration config changes (host,
 * token, GitHub App config, Bitbucket auth mode, etc.) made via the Platform
 * Settings "Git Providers" page.
 *
 * This mirrors the backend's `ScmIntegrationConfigProviderService`
 * `onChange()`/`notifyChange()` pair (see specs/991-dynamic-git-integrations),
 * which lets `coreServices.scmIntegrations` and the URL readers rebuild
 * immediately on save/delete instead of polling. On the frontend, the app's
 * `scmIntegrationsApiRef` override (see
 * `packages/app/src/dynamicScmIntegrationsApi.ts`) subscribes via
 * `onChange` to refresh immediately, instead of waiting for its periodic
 * poll.
 */
export interface GitIntegrationsChangeNotifier {
  /** Registers a listener, fired on every `notifyChange()` call. Returns an unsubscribe function. */
  onChange(listener: () => void): () => void;
  /** Fires all registered listeners. Called after a successful save/delete. */
  notifyChange(): void;
}

function createGitIntegrationsChangeNotifier(): GitIntegrationsChangeNotifier {
  const listeners = new Set<() => void>();
  return {
    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    notifyChange() {
      for (const listener of listeners) listener();
    },
  };
}

/**
 * Singleton notifier shared between the Platform Settings "Git Providers"
 * page (which calls `notifyChange()` after a successful save/delete) and any
 * frontend consumer that caches git integration config (which calls
 * `onChange()` to know when to refresh).
 */
export const gitIntegrationsChangeNotifier: GitIntegrationsChangeNotifier =
  createGitIntegrationsChangeNotifier();
